import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_OPTIONS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
} from "common/Constants/AccessStatus.constants";

export {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
};

export const PLATFORM_CUSTOMER_ACCESS_STATUS_OPTIONS =
  CUSTOMER_ACCESS_STATUS_OPTIONS;

export const DEFAULT_CUSTOMER_ROLE_OPTIONS =
  INFRAMARK_CONSTANTS?.users?.constants?.customerAccessRoles ?? [];

export const DEFAULT_PLATFORM_ROLE_HINTS = {
  none: "No platform-level access. They may still hold customer roles.",
  platformadmin:
    "Full platform administration — customers, configurations and user access.",
  platform_admin:
    "Full platform administration — customers, configurations and user access.",
};

export const normalizePlatformUsername = (username) =>
  String(username ?? "").trim();

export const PLATFORM_USER_ACCESS_UI = {
  pageTitle: "User access",
  pageSubtitle: "Platform roles and access grants.",
  inviteUserLabel: "Invite user",
  selectedUserTitle: "Selected user",
  inviteDrawerTitle: "Invite user",
  closeSelectedUserPanelAriaLabel: "Close selected user panel",
  closeInviteUserPanelAriaLabel: "Close invite user panel",
  cancelLabel: "Cancel",
  sendInviteLabel: "Send invite",
  inviteDescription:
    "Platform administration is for Azuria Water Solutions Inc use only. Platform admin is a global role — it can reach every customer and every user.",
  inviteRequiredHint: "(required)",
  inviteEmailHint:
    "",
  inviteRoleSummaryNone:
    "",
  inviteRoleSummaryPlatformAdmin:
    "",
  saveChangesLabel: "Save changes",
  customerAccessTitle: "Customer access",
  customerAccessDescription:
    "Select which customers this invited user can access and assign one or more customer roles for each.",
  addCustomerLabel: "+ Add customer",
  actionColumnLabel: "Action",
  removeCustomerRowAriaLabel: "Remove customer row",
  invalidEmailError: "Please provide a valid email address.",
  inviteEmailAlreadyExistsError:
    "This email is already registered. Use a different address or manage the existing user.",
  inviteEmailValidationFailedError: "Unable to validate email exists or not.",
  updateSuccessMessage: "User access updated successfully.",
  saveDisabledNotActiveHint:
    "Save is not available while the user account status is Invited.",
  customerAccessCustomerSelectPlaceholder: "Select customer",
  customerAccessCustomerRequiredError: "Select a customer.",
  customerAccessDuplicateCustomerError:
    "This customer is already assigned. Choose a different customer.",
  customerAccessRolesRequiredError: "Select at least one role.",
  customerAccessRequiredWhenNonePlatformRoleError:
    "When Platform role is None, add at least one customer with at least one role.",
  customerAccessOptionalWhenPlatformAdminHint:
    "For Platform Admin, customer access is optional.",
  customerDeactivatedWarning: "Customer is deactivated",
  defaultPlatformRole: "None",
  inviteEmailLabel: "Email address",
  inviteEmailPlaceholder: "Email address",
  invitePlatformRoleLabel: "Platform role",
  tableColumns: {
    email: "Email",
    firstName: "First Name",
    lastName: "Last Name",
    phone: "Phone",
    platformRole: "Platform Role",
    accountStatus: "Account Status",
    action: "Action",
    customer: "Customer",
    roles: "Roles",
    customerAccess: "Access",
  },
  clearFiltersLabel: "Clear filters",
  emptyStateTitle: "No users match these filters",
  emptyStateHint: "Clear one or more filters to widen the search.",
};

export const buildPlatformUsersResultSummary = (
  shownCount,
  filtersActive,
  grandTotal
) => {
  const shown = Number(shownCount) || 0;
  const total = Number(grandTotal) || 0;
  if (filtersActive) {
    if (total > 0) {
      return `${shown} of ${total} platform users match`;
    }
    return `${shown} platform users match`;
  }
  return `${shown} platform users`;
};

export const getPlatformAccountStatusTone = (status) => {
  const normalized = normalizeCustomerAccessStatus(status);
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.ACTIVE)) {
    return "ok";
  }
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.INVITED)) {
    return "info";
  }
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.SUSPENDED)) {
    return "warn";
  }
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.REVOKED)) {
    return "crit";
  }
  return "neutral";
};

export const PLATFORM_USER_LIST_ACTION = {
  SUSPEND: "suspend",
  RESEND: "resend",
  RESTORE: "restore",
};

export const normalizePlatformUserAccountStatus = (raw) =>
  normalizeCustomerAccessStatus(raw);

export const getPlatformUserListRowAction = (status) => {
  const normalized = normalizePlatformUserAccountStatus(status);
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.ACTIVE)) {
    return {
      label: "Suspend",
      action: PLATFORM_USER_LIST_ACTION.SUSPEND,
    };
  }
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.INVITED)) {
    return {
      label: "Resend",
      action: PLATFORM_USER_LIST_ACTION.RESEND,
    };
  }
  if (isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.SUSPENDED)) {
    return {
      label: "Restore",
      action: PLATFORM_USER_LIST_ACTION.RESTORE,
    };
  }
  return null;
};

export const PLATFORM_USER_ACCESS_LAYOUT = {
  drawerWidth: "60vw",
};

export const PLATFORM_USER_ACCESS_FILTER_ALL = "__all__";

export const DEFAULT_PLATFORM_LIST_FILTERS = {
  username: "",
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  platformRole: "",
  accountStatus: "",
};

export const normalizePlatformListFilters = (rawColumnFilters = {}) => {
  const clean = (value) => {
    if (
      value == null ||
      value === "" ||
      value === PLATFORM_USER_ACCESS_FILTER_ALL
    ) {
      return "";
    }
    return String(value).trim();
  };
  return {
    username: clean(rawColumnFilters.username),
    email: clean(rawColumnFilters.email),
    firstName: clean(rawColumnFilters.firstName),
    lastName: clean(rawColumnFilters.lastName),
    phone: clean(rawColumnFilters.phone),
    platformRole: clean(rawColumnFilters.platformRole),
    accountStatus: clean(rawColumnFilters.status),
  };
};

export const mapPlatformListFiltersToColumnFilters = (
  listFilters = DEFAULT_PLATFORM_LIST_FILTERS
) => ({
  username: listFilters.username ?? "",
  email: listFilters.email ?? "",
  firstName: listFilters.firstName ?? "",
  lastName: listFilters.lastName ?? "",
  phone: listFilters.phone ?? "",
  platformRole: listFilters.platformRole ?? "",
  status: listFilters.accountStatus ?? "",
});

export const PLATFORM_USER_ACCESS_ACCOUNT_STATUS_FILTER_OPTIONS =
  CUSTOMER_ACCESS_STATUS_OPTIONS;

export const isPlatformUserAccountEditable = (status) => {
  const normalized = normalizePlatformUserAccountStatus(status);
  return (
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.ACTIVE) ||
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.SUSPENDED)
  );
};

export const isPlatformUserEmailClickable = (status) =>
  !isCustomerAccessStatus(
    normalizePlatformUserAccountStatus(status),
    CUSTOMER_ACCESS_STATUS.INVITED
  );

const normalizeRolesForCompare = (roles) =>
  (roles ?? [])
    .map((role) =>
      typeof role === "string" ? role : role?.value || role?.label
    )
    .filter(Boolean)
    .sort();

const customerAccessCompareSignature = (row) => ({
  customerId: String(row?.customerId ?? ""),
  customerCode: String(row?.customerCode ?? ""),
  roles: normalizeRolesForCompare(row?.roles).join("\0"),
});

export const isCustomerAccessRowChanged = (original, next) => {
  if (!original) {
    return true;
  }
  return (
    JSON.stringify(customerAccessCompareSignature(original)) !==
    JSON.stringify(customerAccessCompareSignature(next))
  );
};
export const hasPlatformRoleChanged = (originalRole, nextRole) =>
  String(originalRole ?? "").trim() !== String(nextRole ?? "").trim();

export const normalizePlatformRoleValue = (platformRole) => {
  if (platformRole == null) {
    return PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
  }
  if (typeof platformRole === "object") {
    const fromObject = platformRole.value ?? platformRole.label;
    if (fromObject != null && String(fromObject).trim() !== "") {
      return String(fromObject).trim();
    }
    return PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
  }
  const normalized = String(platformRole).trim();
  return normalized || PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
};

export const isNonePlatformRole = (platformRole) => {
  const normalized = normalizePlatformRoleValue(platformRole);
  return normalized === PLATFORM_USER_ACCESS_UI.defaultPlatformRole;
};

export const isPlatformAdminPlatformRole = (platformRole) =>
  normalizePlatformRoleValue(platformRole).toLowerCase() === "platformadmin";

const isBlankCustomerAccessRow = (row) => {
  const customerId = String(row?.customerId ?? "").trim();
  const roles = normalizeRolesForCompare(row?.roles);
  return !customerId && roles.length === 0;
};

export const hasCompletePlatformCustomerAccessRow = (row) => {
  const customerId = String(row?.customerId ?? "").trim();
  const roles = normalizeRolesForCompare(row?.roles);
  return Boolean(customerId) && roles.length > 0;
};

export const shouldShowPlatformAdminOptionalCustomerHint = (platformRole) =>
  isPlatformAdminPlatformRole(platformRole);

const getDuplicateCustomerRowIndexes = (rows) => {
  const indexesByCustomerId = new Map();
  (rows ?? []).forEach((row, index) => {
    const customerId = String(row?.customerId ?? "").trim();
    if (!customerId) {
      return;
    }
    const existing = indexesByCustomerId.get(customerId) ?? [];
    existing.push(index);
    indexesByCustomerId.set(customerId, existing);
  });
  const duplicateIndexes = new Set();
  indexesByCustomerId.forEach((indexes) => {
    if (indexes.length > 1) {
      indexes.forEach((index) => duplicateIndexes.add(index));
    }
  });
  return duplicateIndexes;
};

export const validatePlatformCustomerAccess = (rows, platformRole) => {
  const resolvedPlatformRole = normalizePlatformRoleValue(platformRole);
  const list = rows ?? [];
  const duplicateCustomerRowIndexes = getDuplicateCustomerRowIndexes(list);
  const rowErrors = list.map((row, index) => {
    if (
      isPlatformAdminPlatformRole(resolvedPlatformRole) &&
      isBlankCustomerAccessRow(row)
    ) {
      return undefined;
    }
    const customerId = String(row?.customerId ?? "").trim();
    const roles = normalizeRolesForCompare(row?.roles);
    const errors = {};
    if (!customerId) {
      errors.customerId =
        PLATFORM_USER_ACCESS_UI.customerAccessCustomerRequiredError;
    } else if (duplicateCustomerRowIndexes.has(index)) {
      errors.customerId =
        PLATFORM_USER_ACCESS_UI.customerAccessDuplicateCustomerError;
    }
    if (roles.length === 0) {
      errors.roles = PLATFORM_USER_ACCESS_UI.customerAccessRolesRequiredError;
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
  });

  const completeRowCount = list.filter(hasCompletePlatformCustomerAccessRow).length;

  const hasRowErrors = rowErrors.some(Boolean);
  const formError =
    isNonePlatformRole(resolvedPlatformRole) &&
    completeRowCount === 0 &&
    !hasRowErrors
      ? PLATFORM_USER_ACCESS_UI.customerAccessRequiredWhenNonePlatformRoleError
      : null;

  return {
    valid: !hasRowErrors && !formError,
    rowErrors,
    formError,
  };
};

export const findActiveCustomerByCustomerId = (customers, customerId) => {
  const key = String(customerId ?? "").trim();
  if (!key) {
    return null;
  }
  return (
    (customers ?? []).find((customer) => {
      return String(customer.customerId) === key;
    }) ?? null
  );
};

export const isCustomerAccessRowDeactivated = (row, activeCustomers = []) => {
  const customerId = row?.customerId;
  if (customerId == null || String(customerId).trim() === "") {
    return false;
  }
  return findActiveCustomerByCustomerId(activeCustomers, customerId) == null;
};

export const buildActiveCustomerSelectOptions = ({
  customers = [],
  selectedAccessRows = [],
} = {}) => {
  const byKey = new Map();
  const addOption = (customerId, label, customerCode = "") => {
    const key = String(customerId).trim();
    if (!key || byKey.has(key)) {
      return;
    }
    byKey.set(key, {
      label: label || key,
      value: customerId,
      customerCode: customerCode,
    });
  };

  (customers ?? []).forEach((customer) => {
    addOption(
      customer.customerId,
      customer.customerName,
      customer.customerCode
    );
  });

  (selectedAccessRows ?? []).forEach((row) => {
    const customerId = row?.customerId;
    if (customerId == null || String(customerId).trim() === "") {
      return;
    }
    const key = String(customerId);
    if (byKey.has(key)) {
      return;
    }
    const match = findActiveCustomerByCustomerId(customers, customerId);
    addOption(
      customerId,
      row.customerName ||
        match?.customerName ||
        key,
      row.customerCode ||
        match?.customerCode
    );
  });

  return Array.from(byKey.values());
};

export const buildCustomerSelectOptionsForRow = (
  allOptions = [],
  rows = [],
  rowIndex = 0
) => {
  const currentRow = rows[rowIndex];
  const currentCustomerId =
    currentRow?.customerId != null && String(currentRow.customerId).trim() !== ""
      ? String(currentRow.customerId)
      : null;
  const usedByOtherRows = new Set(
    (rows ?? [])
      .map((row, index) => {
        if (index === rowIndex) {
          return null;
        }
        const customerId = row?.customerId;
        if (customerId == null || String(customerId).trim() === "") {
          return null;
        }
        return String(customerId);
      })
      .filter(Boolean)
  );

  return (allOptions ?? []).filter((option) => {
    const optionKey = String(option?.value ?? "");
    if (!optionKey) {
      return false;
    }
    if (currentCustomerId && optionKey === currentCustomerId) {
      return true;
    }
    return !usedByOtherRows.has(optionKey);
  });
};

export const enrichCustomerAccessRowFromActiveCustomer = (row, customers) => {
  const match = findActiveCustomerByCustomerId(customers, row?.customerId);
  if (!match) {
    return row;
  }
  const customerId = match.customerId ?? row.customerId;
  const customerCode =
    match.customerCode ||
    row.customerCode;
  return {
    ...row,
    customerId,
    customerCode, 
    customerName: match.customerName,
  };
};

export const hasCustomerAccessChanged = (originalRows, nextRows) => {
  const toKey = (row) => customerAccessCompareSignature(row);
  const original = (originalRows ?? [])
    .filter((row) => row?.customerId != null && String(row.customerId).trim() !== "")
    .map(toKey)
    .sort((a, b) => a.customerId.localeCompare(b.customerId));
  const next = (nextRows ?? [])
    .filter((row) => row?.customerId != null && String(row.customerId).trim() !== "")
    .map(toKey)
    .sort((a, b) => a.customerId.localeCompare(b.customerId));
  return JSON.stringify(original) !== JSON.stringify(next);
};
